import { injectAPISession, IUserProperties } from '@monofe/apiservice';
import { TYPES as API_TYPES } from '@monofe/apiservice/session/interface/bind';
import { TYPES as APPSTATE_TYPES } from '@monofe/appstate';
import type { IContainer } from '@monofe/di';
import { buildBindable } from '@monofe/di';
import type { ILog, LANGUAGE_CODE } from '@monofe/util';
import { injectLog, injectTranslationStorage, TYPES as UTIL_TYPES } from '@monofe/util';
import type { IServiceWorkerController } from '@monofe/worker';
import {
    injectServiceWorkerController,
    IServiceWorkerStatus,
    TYPES as WORKER_TYPES,
} from '@monofe/worker';
import byteSizeToDescription from '../common/byteSizeToDescription';
import { injectDialog } from '../dialog';
import { TYPES as DIALOG_TYPES } from '../dialog/interface';
import { injectToast, TYPES as TOAST_TYPES } from '../toast/interface';
import type { IDataResolver, IDownloadTour } from './interface';
import {
    bindDownloadTour,
    TYPES as DOWNLOADTOUR_TYPES,
    IDownloadState,
    IMediaType,
    injectDownloadTour,
    unbindDownloadTour,
} from './interface';
import type { IModuleState } from './state';
import state from './state';
import type { IModuleDialog } from './util/dialog';
import dialog from './util/dialog';
import onAccept from './util/onAccept';
import onDecline from './util/onDecline';
import type { IModuleSession } from './util/session';
import session from './util/session';
import type { IModuleToast } from './util/toast';
import toast from './util/toast';
import type { IModuleTranslate } from './util/translate';
import translate, { TranslationKey } from './util/translate';
import ProgressBar from './views/ProgressBar';

const DEFAULT_LANGUAGE_ID = 1; /* Default to English=1 */
const DEFAULT_LANGUAGE_CODE = 'EN'; /* As above */

async function _updateDownloadSizeText(translater: IModuleTranslate, resolver: IDataResolver) {
    const sites = await resolver.all('ISiteDto');

    const downloadList = await resolver.downloadList();

    const allUrls = await Promise.all(
        downloadList.map(medium => resolver.urlsForMedium(medium, [IMediaType.Download])),
    );

    /* Remove any duplicates */
    const downloadUrls = [...new Set(allUrls.flat())];

    const requiredSizes = await Promise.all(
        downloadUrls.map(url => resolver.urlContentLength(url)),
    );
    const requiredSize = requiredSizes.reduce((previous, current) => previous + current, 0);

    const sizeDesc = byteSizeToDescription(requiredSize);

    translater.updateDefaultParams(TranslationKey.DOWNLOAD_FAILED_NOT_ENOUGH_SPACE, [
        sites.at(0)?.name ?? 'Unknown',
        sizeDesc,
    ]);

    translater.updateDefaultParams(TranslationKey.DOWNLOAD_POPUP_DESCRIPTION, [
        sites.at(0)?.name ?? 'Unknown',
        sizeDesc,
    ]);
}

export interface IModuleDependencies {
    session: Readonly<IModuleSession>;
    dialog: Readonly<IModuleDialog>;
    store: Readonly<IModuleState>;
    toast: Readonly<IModuleToast>;
    translate: Readonly<IModuleTranslate>;
    log: Readonly<ILog>;
    workerController: Readonly<IServiceWorkerController>;
    resolver: Readonly<IDataResolver>;
}

function _buildLocalDependencies(
    container: IContainer,
    dependencies: Partial<IModuleDependencies> & Pick<IModuleDependencies, 'resolver'>,
): Promise<IModuleDependencies> {
    const translationStorage = injectTranslationStorage(container);
    const workerController = injectServiceWorkerController(container);
    const log = injectLog(container);
    const sessionFetcher = injectAPISession(container);
    const dialogModule = injectDialog(container);
    const toastModule = injectToast(container);

    const sessionModule = dependencies.session ?? session(sessionFetcher);
    const translater = dependencies.translate ?? translate(translationStorage);
    const toaster = dependencies.toast ?? toast(translater, toastModule);
    const store = dependencies.store ?? state(container);
    const resolver = dependencies.resolver;

    const dialogue = dialog(
        {
            id: 'Download-Tour',
            onAccept: () =>
                onAccept({
                    session: sessionModule,
                    toast: toaster,
                    dialog: dialogue,
                    serviceWorker: workerController,
                    store,
                    resolver,
                    log,
                }),
            onDecline: () => {
                onDecline({ store, dialog: dialogue });
            },
        },
        dialogModule,
    );

    return Promise.resolve({
        session: sessionModule,
        dialog: dialogue,
        store,
        toast: toaster,
        translate: translater,
        log,
        workerController,
        resolver,
    });
}

function _buildModule(deps: IModuleDependencies): IDownloadTour {
    const Renderer = (): JSX.Element | null => {
        const { progressVisible, progressBar } = deps.store.useState();
        return <ProgressBar visible={progressVisible} progress={progressBar} />;
    };

    return {
        renderer: Renderer,
        show: () => {
            deps.dialog.show();
        },
        hide: () => {
            deps.dialog.hide();
            deps.store.action.hideProgressBar();
            deps.toast.hideAll();
        },
        isShowing: () =>
            deps.dialog.isShowing() ||
            deps.toast.isAnyShowing() ||
            deps.store.getState().progressVisible,
        isShowable: () => {
            const { userSession, languageId, tourId } = deps.store.getState();
            const worker = deps.workerController.active();
            return (
                userSession.length > 0 &&
                languageId > 0 &&
                tourId > 0 &&
                (worker?.status === IServiceWorkerStatus.Ready ||
                    worker?.status === IServiceWorkerStatus.Running)
            );
        },
        state: () => {
            let newState = IDownloadState.Undef;

            if (deps.store.getState().languageId <= 0 || deps.store.getState().tourId <= 0) {
                newState = IDownloadState.AwaitingLanguageTour;
            } else if (deps.dialog.isShowing()) {
                newState = IDownloadState.PopUpAsk;
            } else if (deps.store.getState().progressVisible) {
                newState = IDownloadState.Downloading;
            } else {
                newState = IDownloadState.Idle;
            }

            return newState;
        },
        dataResolver: () => deps.resolver,
    };
}

function _initializeState(deps: IModuleDependencies) {
    const activeSession = deps.session.active();

    if (activeSession) {
        const languageId =
            activeSession.state.getProperty<number>(IUserProperties.LanguageId) ??
            DEFAULT_LANGUAGE_ID;
        const tourId = activeSession.state.getProperty<number>(IUserProperties.TourId) ?? -1;
        deps.store.action.updateSession(activeSession.name);
        deps.store.action.updateLanguageId(languageId);
        deps.store.action.updateTourId(tourId);

        if (tourId > 0) {
            _updateDownloadSizeText(deps.translate, deps.resolver).catch((error: Error) => {
                deps.log.error(`Failed to update translation: ${error.message}`);
            });
        }
    }

    /* Force translation update */
    deps.translate.updateLanguage(deps.translate.language());
}

function _addEffects(deps: IModuleDependencies) {
    const updateTranslations = () => {
        deps.dialog.updateText({
            title: deps.translate.translate(TranslationKey.DOWNLOAD_POPUP_DESCRIPTION),
            textAccept: deps.translate.translate(TranslationKey.DOWNLOAD_POPUP_ACCEPT),
            textDecline: deps.translate.translate(TranslationKey.DOWNLOAD_POPUP_DECLINE),
        });
    };

    deps.translate.subscribeLanguageChanged(updateTranslations);

    deps.translate.subscribeTranslationChanged(
        TranslationKey.DOWNLOAD_POPUP_DESCRIPTION,
        (module: IModuleTranslate, language: LANGUAGE_CODE) => {
            updateTranslations();
        },
    );

    deps.session.subscribeOnSessionChange(sessionCallback => {
        deps.store.action.updateSession(sessionCallback?.name ?? '');
    });

    deps.session.subscribeOnLanguageChange(async languageId => {
        deps.store.action.updateLanguageId(languageId);
        const languageCode =
            languageId > 0
                ? (await deps.resolver.get('ILanguageDto', languageId)).name
                : DEFAULT_LANGUAGE_CODE;

        deps.translate.updateLanguage(languageCode.toLowerCase() as LANGUAGE_CODE);
    });

    deps.session.subscribeOnTourChange(async tourId => {
        deps.store.action.updateTourId(tourId);
        await _updateDownloadSizeText(deps.translate, deps.resolver);
    });
}

export async function binder(
    container: IContainer,
    dependencies: Partial<IModuleDependencies>,
): Promise<Readonly<IDownloadTour>> {
    const buildDefaultResolver = async () => {
        const module = await import('./defaultResolver');
        const sessionModule = session(injectAPISession(container));
        return module.default(sessionModule);
    };

    const deps = await _buildLocalDependencies(container, {
        ...dependencies,
        resolver: dependencies.resolver ?? (await buildDefaultResolver()),
    });

    const module = _buildModule(deps);

    _addEffects(deps);
    _initializeState(deps);

    return Object.freeze(module);
}

export default (function () {
    let localDependencies: Partial<IModuleDependencies> = {};

    const result = buildBindable<IDownloadTour>({
        id: DOWNLOADTOUR_TYPES.IDownloadTour,
        dependencies: [
            APPSTATE_TYPES.IStoresReducer,
            DIALOG_TYPES.IDialog,
            TOAST_TYPES.IToast,
            UTIL_TYPES.ITranslationStorage,
            WORKER_TYPES.IServiceWorkerController,
            UTIL_TYPES.ILog,
            API_TYPES.ISessionInstantiator,
        ],
        bind: bindDownloadTour,
        remove: unbindDownloadTour,
        inject: injectDownloadTour,
        build: container => binder(container, localDependencies),
    });

    return Object.freeze({
        ...result,
        setBuildDependencies: (deps: Readonly<Partial<IModuleDependencies>>) =>
            (localDependencies = deps),
    });
})();
